import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'

import Pagination from '@/components/ui/pagination/Pagination'
import type { InsAuditLog } from '@/core/models/ins-audit.model'

import { insAuditOperationMessages } from '../lib/ins-audit-labels'
import { IdentityAuditChangeDetails } from './IdentityAuditChangeDetails'

type IdentityAuditOperationsTableProps = {
  isError: boolean
  isFetching: boolean
  itemsPerPage: number
  logs: InsAuditLog[]
  onItemsPerPageChange: (itemsPerPage: number) => void
  onPageChange: (page: number) => void
  page: number
  rowsPerPageOptions: number[]
  total: number
}

function buildStaffLabel(log: InsAuditLog): string {
  const userInfo = log.staff?.userInfo
  return `${userInfo?.surname ?? ''} ${userInfo?.name ?? ''}`.trim()
}

export function IdentityAuditOperationsTable({
  isError,
  isFetching,
  itemsPerPage,
  logs,
  onItemsPerPageChange,
  onPageChange,
  page,
  rowsPerPageOptions,
  total,
}: IdentityAuditOperationsTableProps) {
  const lingui = useLingui()
  const startIndex = logs.length ? (page - 1) * itemsPerPage + 1 : 0
  const endIndex = logs.length ? startIndex + logs.length - 1 : 0

  if (isFetching) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress aria-label={lingui._(t`Loading`)} />
      </Box>
    )
  }

  if (isError) {
    return (
      <Alert severity="error">
        <Trans>Unable to load identity audit history.</Trans>
      </Alert>
    )
  }

  if (logs.length === 0) {
    return (
      <Alert severity="info">
        <Trans>No identity audit history.</Trans>
      </Alert>
    )
  }

  return (
    <Stack spacing={2}>
      <TableContainer sx={{ maxHeight: '60vh' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <Trans>Date and time</Trans>
              </TableCell>
              <TableCell>
                <Trans>Staff</Trans>
              </TableCell>
              <TableCell>
                <Trans>Operation</Trans>
              </TableCell>
              <TableCell>
                <Trans>Change details</Trans>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log._id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {dayjs(log.createdAt).isValid() ? dayjs(log.createdAt).format('DD/MM/YYYY HH:mm') : ''}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{buildStaffLabel(log)}</TableCell>
                <TableCell>
                  <Chip color="primary" label={lingui._(insAuditOperationMessages[log.operation])} size="small" variant="outlined" />
                </TableCell>
                <TableCell sx={{ minWidth: 280 }}>
                  <IdentityAuditChangeDetails changes={log.changes} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={2}>
        <Typography component="div" variant="body2" color="text.secondary">
          <Trans>
            Showing {startIndex}–{endIndex} of {total} results
          </Trans>
        </Typography>
        <Pagination
          color="primary"
          defaultItemsPerPage={itemsPerPage}
          itemsPerPageOptions={rowsPerPageOptions}
          onItemsPerPageChange={onItemsPerPageChange}
          onPageChange={onPageChange}
          page={page}
          shape="rounded"
          size="small"
          totalItems={total}
          variant="outlined"
        />
      </Stack>
    </Stack>
  )
}
